import { useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Link2, Loader2, ImagePlus, X } from "lucide-react";

const BASE = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
export const fileSrc = (u) => (u && u.startsWith("/api/") ? `${BASE}${u}` : u);
const isHtml = (s) => /<\/?[a-z][\s\S]*>/i.test(s || "");

// Paste a link (e.g. Mailchimp) → its text and images are copied into the announcement
export const ImportLinkBox = ({ onImported }) => {
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${BASE}/api/announcements/import-link`, { url }, auth());
      onImported(r.data);
      toast.success("Page copied into the announcement — review it below");
      setUrl("");
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't import that link"); } finally { setBusy(false); }
  };
  return (
    <div className="flex gap-2" data-testid="announcement-import-box">
      <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="Paste a link (e.g. mailchi.mp/...) to copy its content" data-testid="announcement-import-url" />
      <Button type="button" variant="outline" onClick={go} disabled={busy || !url.trim()} data-testid="announcement-import-btn">
        {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Link2 className="w-4 h-4 mr-1" />}Import
      </Button>
    </div>
  );
};

export const GalleryUploader = ({ images, onChange }) => {
  const [busy, setBusy] = useState(false);
  const upload = async (files) => {
    setBusy(true);
    const added = [];
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      try { added.push((await axios.post(`${BASE}/api/upload?folder=announcements`, fd, auth())).data.url); }
      catch { toast.error(`Couldn't upload ${f.name}`); }
    }
    onChange([...(images || []), ...added]);
    setBusy(false);
  };
  return (
    <div className="space-y-2" data-testid="announcement-gallery-uploader">
      <div className="flex flex-wrap gap-2">
        {(images || []).map((u, i) => (
          <div key={u} className="relative w-20 h-20 rounded-md overflow-hidden border border-slate-200">
            <img src={fileSrc(u)} alt="" className="w-full h-full object-cover" />
            <button type="button" onClick={() => onChange(images.filter((_, n) => n !== i))} className="absolute top-0.5 right-0.5 bg-white/90 rounded-full p-0.5" data-testid={`announcement-gallery-remove-${i}`}><X className="w-3 h-3" /></button>
          </div>
        ))}
        <label className="w-20 h-20 rounded-md border-2 border-dashed border-slate-300 flex flex-col items-center justify-center text-xs text-slate-500 cursor-pointer hover:border-teal" data-testid="announcement-gallery-add">
          {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <ImagePlus className="w-5 h-5" />}Add
          <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => upload([...e.target.files])} />
        </label>
      </div>
    </div>
  );
};

export const AnnouncementBody = ({ a }) => {
  const [open, setOpen] = useState(null);
  const html = (a.content || "").replace(/src="\/api\//g, `src="${BASE}/api/`);
  return (
    <div className="space-y-3">
      {isHtml(a.content)
        ? <div className="prose prose-sm max-w-none text-slate-700 [&_img]:max-w-full [&_img]:h-auto [&_table]:max-w-full" dangerouslySetInnerHTML={{ __html: html }} data-testid="announcement-body" />
        : <p className="text-slate-600 whitespace-pre-wrap" data-testid="announcement-body">{a.content}</p>}
      {a.images?.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="announcement-gallery">
          {a.images.map((u) => (
            <button key={u} type="button" onClick={() => setOpen(u)} className="aspect-video rounded-lg overflow-hidden border border-slate-200 hover:opacity-90 transition-opacity">
              <img src={fileSrc(u)} alt="" className="w-full h-full object-cover" loading="lazy" />
            </button>
          ))}
        </div>
      )}
      {a.source_url && <a href={a.source_url} target="_blank" rel="noreferrer" className="text-xs text-teal hover:underline" data-testid="announcement-source-link">View original</a>}
      <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent className="max-w-4xl p-2"><img src={fileSrc(open)} alt="" className="w-full h-auto rounded" /></DialogContent>
      </Dialog>
    </div>
  );
};
